import { istFeiertag } from './feiertage'

/**
 * Tagesgrenzen und Tagesschlüssel – **im Anwendungscode, nie in SQL**.
 *
 * Hintergrund [E-17](../../../docs/ENTSCHEIDUNGEN.md): Zeitstempel liegen in UTC,
 * der Datenbankserver läuft auf lokaler Zeit. `TIMESTAMPDIFF(…, NOW())` lieferte
 * 120 Minuten für einen Abgleich, der eine Minute alt war.
 *
 * Für die Daily-Auswertung ist das keine Feinheit: „was ist gestern passiert"
 * braucht Tagesgrenzen in **lokaler** Zeit. Ein Ereignis um 01:30 unserer Zeit
 * liegt in UTC noch am Vortag. Wer die Grenzen in SQL bildet, ordnet es dem
 * falschen Tag zu – und die Zahlen sehen dabei plausibel aus.
 */

/**
 * Zeitzone des Teams. Bewusst fest und nicht aus der Serverumgebung gelesen:
 * die Auswertung soll für alle dasselbe zeigen, unabhängig davon, wo der Server
 * steht oder wer sie aufruft.
 */
export const ZEITZONE = 'Europe/Berlin'

const TAG_FORMAT = new Intl.DateTimeFormat('sv-SE', {
  timeZone: ZEITZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/**
 * Tagesschlüssel `JJJJ-MM-TT` in lokaler Zeit.
 *
 * `sv-SE` als Gebietsschema, weil dessen Datumsformat schon `JJJJ-MM-TT` ist –
 * das erspart Zusammenbauen aus Teilen und damit eine Fehlerquelle.
 */
export function tagesschluessel(zeitpunkt: Date): string {
  return TAG_FORMAT.format(zeitpunkt)
}

/** Verschiebung der Zeitzone gegenüber UTC an einem bestimmten Zeitpunkt, in Minuten. */
function versatzMinuten(zeitpunkt: Date): number {
  // Denselben Moment einmal als UTC und einmal in der Zielzone formatieren; die
  // Differenz ist der Versatz. Funktioniert auch über Sommerzeitgrenzen.
  const alsZone = new Date(
    new Intl.DateTimeFormat('sv-SE', {
      timeZone: ZEITZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .format(zeitpunkt)
      .replace(' ', 'T') + 'Z',
  )
  return (alsZone.getTime() - zeitpunkt.getTime()) / 60000
}

/**
 * Beginn eines lokalen Tages als UTC-Zeitpunkt.
 *
 * `tagesbeginn('2026-07-30')` liefert den Moment, an dem in Berlin der
 * 30.07.2026 um 00:00 begann – als `Date`, das direkt in eine Abfrage geht.
 */
export function tagesbeginn(tag: string): Date {
  const [jahr, monat, tagZahl] = tag.split('-').map(Number)
  if (!jahr || !monat || !tagZahl) throw new Error(`Unbrauchbarer Tag: ${tag}`)

  // Erst naiv als UTC annehmen, dann um den Versatz an genau diesem Tag
  // korrigieren. Zwei Schritte, weil der Versatz selbst vom Datum abhängt
  // (Sommerzeit).
  const naiv = new Date(Date.UTC(jahr, monat - 1, tagZahl, 0, 0, 0, 0))
  const korrigiert = new Date(naiv.getTime() - versatzMinuten(naiv) * 60000)
  // Zweite Runde fängt den Fall ab, dass der Versatz an der Grenze wechselt.
  return new Date(naiv.getTime() - versatzMinuten(korrigiert) * 60000)
}

/** Ende eines lokalen Tages, exklusiv – also der Beginn des Folgetags. */
export function tagesende(tag: string): Date {
  const beginn = tagesbeginn(tag)
  // 26 Stunden weiter und dann auf Tagesbeginn normalisieren: überspringt auch
  // 23- und 25-Stunden-Tage an Sommerzeitgrenzen zuverlässig.
  return tagesbeginn(tagesschluessel(new Date(beginn.getTime() + 26 * 3600_000)))
}

/**
 * Die letzten `anzahl` Tage bis einschließlich `bis`, als Tagesschlüssel.
 * Ältester zuerst – so wie ein Balkendiagramm gelesen wird.
 */
export function letzteTage(anzahl: number, bis: Date = new Date()): string[] {
  const tage: string[] = []
  for (let i = anzahl - 1; i >= 0; i--) {
    tage.push(tagesschluessel(new Date(bis.getTime() - i * 86_400_000)))
  }
  return tage
}

/**
 * Wochentag eines Tagesschlüssels: 0 = Sonntag, 6 = Samstag.
 *
 * Gerechnet direkt auf dem Kalenderdatum, **nicht** über `tagesbeginn`. Der
 * Umweg über einen Zeitpunkt war falsch: `tagesbeginn('2026-07-30')` ist
 * `2026-07-29T22:00Z`, und dessen `getUTCDay()` liefert den Mittwoch des 29.
 * statt den Donnerstag des 30. – jeder Wochentag um eins verschoben, den ganzen
 * Sommer über. Ein Tagesschlüssel ist ein Kalenderdatum ohne Uhrzeit; er braucht
 * keine Zeitzone.
 */
export function wochentag(tag: string): number {
  const [jahr, monat, tagZahl] = tag.split('-').map(Number)
  if (!jahr || !monat || !tagZahl) throw new Error(`Unbrauchbarer Tag: ${tag}`)
  return new Date(Date.UTC(jahr, monat - 1, tagZahl)).getUTCDay()
}

/** Samstag oder Sonntag. Für „nur Arbeitstage zeigen" in der Daily-Auswertung. */
export function istWochenende(tag: string): boolean {
  const w = wochentag(tag)
  return w === 0 || w === 6
}

/* ── Monat und Quartal als Längen ─────────────────────────────────────────
 *
 * Nils, 21.08.2026: *„Monat heißt immer 30 Tage, Quartal eben 3 Monate. Sonst sind ja manchmal
 * 10 Tage mehr als Monat oder Quartal."*
 *
 * Ein Monat ist im Kalender 28 bis 31 Tage lang und ein Quartal 90 bis 92 – gerechnet wird
 * hier trotzdem mit festen Längen. Ein Zeitfenster ist eine **Frist**, keine Kalenderaussage:
 * „innerhalb eines Monats" soll am 1. Februar dasselbe bedeuten wie am 1. Juli, und die
 * Reihenfolge der Stufen darf nicht davon abhängen, in welchem Monat jemand sie setzt.
 */

/** Ein Monat als Frist – 30 Tage, unabhängig vom Kalendermonat. */
export const MONAT_TAGE = 30

/** Ein Quartal als Frist – drei Monate zu je 30 Tagen. */
export const QUARTAL_TAGE = 3 * MONAT_TAGE

/**
 * `anzahl` **Kalendertage** nach `d`, als Kalendertag ohne Uhrzeit.
 *
 * Bewusst über `setDate` und nicht über `+ n * 86_400_000`: an den Sommerzeitgrenzen ist ein
 * Tag 23 oder 25 Stunden lang, und 30 Tage in Millisekunden landen dort auf dem Vortag.
 */
export function tageSpaeter(d: Date, anzahl: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + anzahl)
}

/* ── Werktage ─────────────────────────────────────────────────────────────
 *
 * Nils, 20.08.2026: *„Bei Zeitfenster die Tage – das sind immer Werktage. Monat, Quartal
 * natürlich nicht, dann aber immer der letzte Werktag in dem Monat oder Quartal."* Seit dem
 * 21.08.2026 sind „Monat" und „Quartal" feste Längen (30 bzw. 90 Tage); die Regel gilt
 * unverändert – das Fristende fällt auf den letzten Werktag davor.
 *
 * Diese Funktionen rechnen auf **`Date` als Kalendertag** (Ortszeit, ohne Uhrzeit) – anders
 * als `istWochenende`, das einen Tagesschlüssel bekommt. Beides nebeneinander, weil die
 * Auswertung mit Schlüsseln arbeitet und die Planung mit `Date`; sie ineinander umzurechnen
 * wäre an jeder Aufrufstelle eine Zeile mehr und eine Fehlerquelle dazu.
 *
 * > **Feiertage kannte Work lange nicht.** Hier stand: *„Ein fest verdrahteter
 * > Feiertagskalender wäre geraten, und für eine Frist, die ohnehin grob ist, ist ein Tag
 * > Ungenauigkeit billiger als ein Kalender, den niemand pflegt."* Der erste Halbsatz stimmte,
 * > der zweite auch – solange Work nur Fristen rechnete.
 * >
 * > **Seit Z-19 zeichnet Work einen Tagesplan**, und dort ist ein Block am 3. Oktober kein Tag
 * > Ungenauigkeit mehr, sondern sichtbar falsch. Eingelöst wird die Zusage in
 * > [`feiertage.ts`](./feiertage.ts) – **gerechnet, nicht gepflegt**: sechs feste Tage und fünf,
 * > die am Osterdatum hängen. Damit bleibt auch der zweite Halbsatz wahr, denn niemand muss
 * > etwas nachtragen.
 */

/**
 * **Montag bis Freitag, ohne gesetzliche Feiertage in NRW.**
 *
 * Diese eine Zeile verschiebt Fristen: Eine Aufgabe mit „5 Tage" über Ostern hinweg ist jetzt
 * zwei Tage später fällig als vorher. Das ist die Absicht – an Karfreitag arbeitet niemand,
 * und eine Frist, die so tut, ist nicht strenger, sondern falsch.
 */
export function istWerktag(d: Date): boolean {
  const w = d.getDay()
  return w !== 0 && w !== 6 && !istFeiertag(d)
}

/** Derselbe Tag ohne Uhrzeit – alle Werktagsrechnungen arbeiten auf Kalendertagen. */
function nurTag(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Der nächste Werktag ab `d`, `d` selbst eingeschlossen. */
export function werktagVorwaerts(d: Date): Date {
  const tag = nurTag(d)
  while (!istWerktag(tag)) tag.setDate(tag.getDate() + 1)
  return tag
}

/** Der letzte Werktag bis `d`, `d` selbst eingeschlossen – für Monats- und Quartalsenden. */
export function werktagRueckwaerts(d: Date): Date {
  const tag = nurTag(d)
  while (!istWerktag(tag)) tag.setDate(tag.getDate() - 1)
  return tag
}

/** `anzahl` Werktage nach `d`. Das Ergebnis ist immer ein Werktag. */
export function werktageSpaeter(d: Date, anzahl: number): Date {
  const tag = nurTag(d)
  for (let offen = anzahl; offen > 0;) {
    tag.setDate(tag.getDate() + 1)
    if (istWerktag(tag)) offen--
  }
  return tag
}

/**
 * Werktage **vor** einem Tag – Hilfsgröße, damit `werktageBis` ohne Schleife auskommt.
 * Der Bezugspunkt ist beliebig; nur Differenzen zweier Indizes werden gelesen.
 */
function werktagsIndex(d: Date): number {
  const tage = Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000)
  // Der 01.01.1970 war ein Donnerstag; +3 verschiebt die Woche so, dass sie montags beginnt.
  const seitMontag = tage + 3
  const wochen = Math.floor(seitMontag / 7)
  const rest = seitMontag - wochen * 7 // 0 = Montag … 6 = Sonntag
  return wochen * 5 + Math.min(rest, 5)
}

/**
 * Werktage von `von` (ausschließlich) bis `bis` (einschließlich); negativ, wenn `bis` davor
 * liegt. „Heute bis morgen früh" ist ein Werktag, „Freitag bis Montag" ebenfalls einer.
 */
export function werktageBis(bis: Date, von: Date): number {
  const naechsterTag = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
  return werktagsIndex(naechsterTag(bis)) - werktagsIndex(naechsterTag(von))
}

/** Kurzbeschriftung für Diagrammachsen, z. B. „Do 30.07.". */
export function tagKurz(tag: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: ZEITZONE,
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(tagesbeginn(tag))
}

/**
 * Datum ohne Jahr, z. B. „14.09." – für Hinweise, die im laufenden Betrieb
 * gelesen werden („abwesend bis 14.09.").
 *
 * Gerechnet **direkt auf dem Kalenderdatum**, nicht über `tagesbeginn`: ein
 * Tagesschlüssel ist ein Datum ohne Uhrzeit und braucht keine Zeitzone. Derselbe
 * Grund wie bei `wochentag`, wo der Umweg über einen Zeitpunkt jeden Wochentag um
 * eins verschoben hat.
 */
export function tagKompakt(tag: string): string {
  const [, monat, tagZahl] = tag.split('-')
  if (!monat || !tagZahl) throw new Error(`Unbrauchbarer Tag: ${tag}`)
  return `${tagZahl}.${monat}.`
}

/**
 * Alter in ganzen Tagen, gerechnet über lokale Tagesgrenzen.
 *
 * Bewusst nicht `(jetzt - dann) / 86400000`: eine Aufgabe von gestern 23:00 ist
 * heute morgens „1 Tag alt", nicht „0 Tage". Für die Liegezeiten-Matrix
 * (Kap. 4.6) ist das der Unterschied zwischen richtiger und falscher Klasse.
 */
export function alterInTagen(seit: Date, jetzt: Date = new Date()): number {
  const a = tagesbeginn(tagesschluessel(seit)).getTime()
  const b = tagesbeginn(tagesschluessel(jetzt)).getTime()
  return Math.round((b - a) / 86_400_000)
}

/**
 * **Ab wann eine Aufgabe zählt** – die eine Antwort auf „seit wann gibt es das?".
 *
 * `sourceCreatedAt` ist das Datum in GitLab bzw. NetCon. Eine **in Work angelegte**
 * Aufgabe hat dort nichts stehen; für sie zählt der Anlagezeitpunkt in Work.
 *
 * Bis zum 04.08.2026 fragten Tabelle, Sortierung und Leitstand direkt
 * `sourceCreatedAt` ab. Folge: eine manuell angelegte Aufgabe zeigte in der Spalte
 * „Alter" ein „–", und in `ORDER BY … ASC` stand sie sogar ganz oben – MariaDB
 * sortiert `NULL` nach vorn, also galt die jüngste Aufgabe als die älteste.
 *
 * Deshalb steht die Regel **hier, einmal**. Wer eine neue Ansicht baut, ruft sie auf
 * und muss nicht wissen, dass es zwei Datumsquellen gibt. Das SQL-Gegenstück heißt
 * `entstandenAm` und liegt in `@netcon-work/db`.
 */
export function entstanden(aufgabe: {
  sourceCreatedAt: Date | string | null
  createdAt: Date | string
}): Date {
  return new Date(aufgabe.sourceCreatedAt ?? aufgabe.createdAt)
}

/** Alter in ganzen Tagen nach derselben Regel wie {@link entstanden}. */
export function alterDerAufgabe(
  aufgabe: { sourceCreatedAt: Date | string | null; createdAt: Date | string },
  jetzt: Date = new Date(),
): number {
  return alterInTagen(entstanden(aufgabe), jetzt)
}

/**
 * Alters-Klassen der Liegezeiten-Matrix (Konzept Kap. 4.6).
 *
 * **Vier statt drei seit E-358** (17.09.2026): „> 7 Tage" nahm alles auf, was je liegen
 * geblieben war – bis zu 916 Tage alte Tickets. Die Spalte sagte damit nichts mehr über Stau.
 * Die Grenze bei 90 ist dieselbe wie für Altlasten im Leitstand.
 */
export const ALTERSKLASSEN = ['unter3', 'drei_bis_sieben', 'bis90', 'ueber90'] as const
export type Altersklasse = (typeof ALTERSKLASSEN)[number]

export const ALTERSKLASSEN_LABELS: Record<Altersklasse, string> = {
  unter3: '< 3 Tage',
  drei_bis_sieben: '3–7 Tage',
  bis90: '8–90 Tage',
  ueber90: '> 90 Tage',
}

export function altersklasse(tage: number): Altersklasse {
  if (tage < 3) return 'unter3'
  if (tage <= 7) return 'drei_bis_sieben'
  if (tage <= 90) return 'bis90'
  return 'ueber90'
}

/* ── Kalenderwochen ──────────────────────────────────────────────────────
 *
 * Standen bis zum 19.08.2026 **privat in `apps/api/src/planung.ts`** und werden seitdem auch
 * von der Zeitachse in der Planung gebraucht. Zwei Fassungen derselben Wochenrechnung wären
 * zwei Gelegenheiten, den Jahreswechsel unterschiedlich zu treffen – und genau dort liegt der
 * Fehler, den niemand bemerkt, bis er im Januar auffällt.
 */

/** Montag der Woche zu einem Datum, als `JJJJ-MM-TT`. */
export function montagVon(d: Date): string {
  const kopie = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const tag = kopie.getUTCDay() || 7
  kopie.setUTCDate(kopie.getUTCDate() - tag + 1)
  return kopie.toISOString().slice(0, 10)
}

/**
 * ISO-Kalenderwoche als `2026-W32`, gerechnet über den **Donnerstag** der Woche.
 *
 * Der Umweg über den Donnerstag ist die ISO-Regel und kein Kunstgriff: eine Woche gehört dem
 * Jahr, in dem ihr Donnerstag liegt. Der 1. Januar 2027 ist ein Freitag – die Woche davor ist
 * `2026-W53`, und wer über den Montag rechnet, bekommt `2027-W01`.
 */
export function isoKw(montag: string): string {
  const d = new Date(`${montag}T00:00:00Z`)
  const donnerstag = new Date(d)
  donnerstag.setUTCDate(d.getUTCDate() + 3)
  const jahresanfang = new Date(Date.UTC(donnerstag.getUTCFullYear(), 0, 1))
  const kw = Math.ceil(((donnerstag.getTime() - jahresanfang.getTime()) / 86_400_000 + 1) / 7)
  return `${donnerstag.getUTCFullYear()}-W${String(kw).padStart(2, '0')}`
}

/** `2026-W32` → `KW 32`, für Spaltenköpfe. */
export function kwKurz(kw: string): string {
  return `KW ${kw.slice(-2)}`
}
